import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import {
    ContactAddressRequest,
    createContactAddress,
    updateContactAddress,
} from 'api/contacts/single';
import { AxiosError } from 'axios';
import { AddressForm } from 'components/address-form/AddressForm';
import { AddressFormValues } from 'components/address-form/types';
import { transformAddressFormToRequest } from 'components/address-form/useAccountAddressForm';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { useGivebutterForm } from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useFormServerErrors } from 'customHooks/useFormServerErrors';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';

interface ContactAddressFormValues extends AddressFormValues {
    type: string;
    isPrimary: boolean;
}

const ContactAddressFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const { accountId } = useCurrentAccount();
    const { address_id: addressId } = useParams<{ address_id?: string }>();
    const address = queryClient
        .getQueryData<Contact>(accountQueryKeys.contactById(accountId, contactId))
        ?.addresses.find(address => address.id.toString() === addressId);
    const isEdit = !!addressId;
    const showPrimary = !isEdit && (contact.addresses ?? []).length > 0;

    const methods = useGivebutterForm<ContactAddressFormValues>({
        defaultValues: {
            line1: address?.address_1 ?? '',
            line2: address?.address_2 ?? '',
            city: address?.city ?? '',
            zip: address?.zipcode ?? '',
            stateUs: address?.country === 'USA' ? (address?.state ?? '') : '',
            stateIntl: address?.country !== 'USA' ? (address?.state ?? '') : '',
            country: address?.country ?? 'USA',
            type: address?.type ?? 'home',
            isPrimary: address?.is_primary ?? false,
        },
    });
    const { setAddressErrors } = useFormServerErrors(methods);
    const { handleSubmit, control, path } = methods;

    const createOrUpdateMutation = useMutation({
        mutationFn: (addressFormValues: ContactAddressRequest) =>
            isEdit
                ? updateContactAddress(accountId, contactId, address!.id, addressFormValues)
                : createContactAddress(accountId, contactId, addressFormValues),
        onSuccess: () => {
            const nextMessage = isEdit ? 'updated' : 'created';
            toast.success(`Address ${nextMessage} successfully!`);
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactById(accountId, contactId),
            });
            goBack();
        },
        onError: (error: AxiosError<{ errors?: { [key: string]: string[] } }>) => {
            const errors = error.response?.data.errors;
            if (errors) {
                setAddressErrors(errors);
            }
        },
    });

    const submit = handleSubmit(values => {
        createOrUpdateMutation.mutate(transformAddressFormToRequest(values));
    });

    return (
        <Form.Provider {...methods}>
            <form className="contents" onSubmit={submit}>
                <Drawer.Header>
                    <Drawer.Title>{isEdit ? 'Edit' : 'New'} Address</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body>
                    <div className={'flex flex-col gap-y-6'}>
                        <AddressForm />
                        <Form.Container>
                            <Form.Label name={path('type')} className={'text-sm font-medium'}>
                                Type
                                <Form.LabelAsterisk />
                            </Form.Label>
                            <Form.SingleSelect
                                control={control}
                                name="type"
                                rules={{ required: 'Address type is required' }}
                            >
                                <SingleSelect.Trigger aria-label="Type" />
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="work">
                                        <SingleSelect.ItemText>Work</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="home">
                                        <SingleSelect.ItemText>Home</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </Form.SingleSelect>
                        </Form.Container>
                        {showPrimary && (
                            <div className={'flex items-center gap-2'}>
                                <Label>Primary</Label>
                                <Form.UseFormCheckboxInput
                                    control={control}
                                    name={`isPrimary`}
                                    disabled={!!address?.is_primary}
                                />
                            </div>
                        )}
                    </div>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            <Form.SubmitButton>{isEdit ? 'Update' : 'Create'}</Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const Root = ContactAddressFormDrawer;

export {
    ContactAddressFormDrawer,
    //
    Root,
};
